import { useQuery } from '@tanstack/react-query'
import { ChevronRight, CircleAlert, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { DecisionSummary } from '@/features/cash/DecisionSummary'
import { useCashFirm } from '@/features/cash/hooks'
import { CashFirmSelect, CashStrip } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { dateWithYear, formatCashAmount } from '@/lib/cash'
import { sortTodo, stripPoints, summarizeWeekly, todoTone } from '@/lib/cash-summary'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { cashApi, type CashTodo } from '@/services/http/cash'

const t = messages.cash.home

/** Maddenin ikinci satırı: tutar (türüne göre etiketli) ve ayrıntı; ham ISO tarih görünmez. */
function todoDetail(item: CashTodo): string {
  const withLabel = t.amountLabel[item.kind]
  const formatted = item.amount == null ? null : formatCashAmount(item.amount)
  const amount = formatted !== null && withLabel ? withLabel(formatted) : formatted
  const detail =
    item.kind === 'liste_onay'
      ? messages.cash.paymentList.status[item.detail ?? '']
      : item.kind === 'eksi_hafta' && item.detail
        ? t.firstWeek(dateWithYear(item.detail))
        : item.detail
  return [amount, detail].filter(Boolean).join(' · ')
}

function TodoRow({ item }: { item: CashTodo }) {
  const tone = todoTone(item)
  const Icon = tone === 'danger' ? CircleAlert : TriangleAlert
  const detail = todoDetail(item)
  return (
    <li>
      <Link
        to={item.link}
        data-kind={item.kind}
        data-tone={tone}
        className={cn(
          'bg-surface hover:bg-surface-muted flex items-center gap-3 rounded-lg border border-l-4 p-3',
          tone === 'danger' ? 'border-l-danger' : 'border-l-warning',
        )}
      >
        <span className="bg-surface-muted min-w-10 rounded-md px-2 py-1 text-center text-sm font-semibold tabular-nums">
          {t.count(item.count)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{t.kinds[item.kind] ?? item.kind}</span>
          {detail && <span className="text-text-secondary text-xs tabular-nums">{detail}</span>}
        </span>
        <span
          className={cn(
            'flex shrink-0 items-center gap-1 text-xs font-medium',
            tone === 'danger' ? 'text-danger' : 'text-warning',
          )}
        >
          <Icon aria-hidden className="size-3.5" />
          {t.tones[tone]}
        </span>
        <ChevronRight className="text-text-secondary size-4 shrink-0" aria-hidden />
      </Link>
    </li>
  )
}

/** /nakit — "Bu hafta yapılacaklar" (docs/NAKIT-PLANI-PLAN.md Faz 4, K1). */
export function HomePage() {
  const { firms, firm, setFirm } = useCashFirm()
  const todo = useQuery({
    queryKey: queryKeys.cash.todo(firm ?? ''),
    queryFn: () => cashApi.todo(firm!),
    enabled: !!firm,
  })
  // Karar özeti ve şerit Haftalık Nakit'in baz sorgusundan türer (aynı önbellek); gelmezse gizlenir.
  const weekly = useQuery({
    queryKey: queryKeys.cash.weekly(firm ?? '', 'baz'),
    queryFn: () => cashApi.weekly(firm!, 'baz'),
    enabled: !!firm,
  })
  const firmLabel = firms.data?.find((f) => f.key === firm)?.label
  const items = todo.data ? sortTodo(todo.data) : []
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={<CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />}
      />
      {weekly.data && <DecisionSummary summary={summarizeWeekly(weekly.data)} />}
      {todo.isError ? (
        <ErrorState error={todo.error} onRetry={() => void todo.refetch()} />
      ) : !todo.data ? (
        <Loading rows={5} />
      ) : items.length === 0 ? (
        <Alert tone="success" title={t.calm} data-testid="cash-todo-calm">
          {t.calmHint}
        </Alert>
      ) : (
        <section className="flex flex-col gap-2" aria-label={t.title}>
          <p className="text-text-secondary flex flex-wrap items-baseline gap-x-3 text-sm">
            <strong className="text-foreground font-semibold">{t.pending(items.length)}</strong>
            {firmLabel && <span>{t.firm(firmLabel)}</span>}
          </p>
          <ul className="flex flex-col gap-2" aria-label={t.title}>
            {items.map((item) => (
              <TodoRow key={item.kind} item={item} />
            ))}
          </ul>
        </section>
      )}
      {weekly.data && (
        <>
          <CashStrip points={stripPoints(weekly.data)} minCash={weekly.data.min_cash} />
          <Link
            to={`/nakit/haftalik?firma=${encodeURIComponent(weekly.data.firm)}`}
            className="self-start text-sm underline"
          >
            {messages.cash.strip.openWeekly}
          </Link>
        </>
      )}
    </div>
  )
}
